import { useState } from 'react';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import {
  createBrowserRouter,
  Navigate,
  useLocation,
  useParams,
  useSearchParams,
  Outlet,
  useNavigate,
} from 'react-router-dom';

import { ProviderReferenceWrapper } from '../../v2/js/pages/Endorsements/ProviderReference/ProviderReferenceWrapper';
import { ProviderSurveyWrapper } from '../../v2/js/pages/Endorsements/ProviderSurvey/ProviderSurveyWrapper';
import { ProviderOnboardingWrapper } from '../../v2/js/pages/Onboarding/Provider/ProviderOnboardingWrapper';
import { AssignmentCreateWrapper } from '../../v2/js/pages/Organization/Assignments/AssignmentCreateWrapper';
import { AssignmentDrawer } from '../../v2/js/pages/Organization/Assignments/AssignmentDrawer';
import { AssignmentsList } from '../../v2/js/pages/Organization/Assignments/AssignmentsList';
import { ClearanceList } from '../../v2/js/pages/Organization/Clearance/ClearanceList';
import { ConnectionDrawer as OrganizationConnectionDrawer } from '../../v2/js/pages/Organization/Connections/ConnectionDrawer';
import { ConnectionsList as OrganizationConnectionsList } from '../../v2/js/pages/Organization/Connections/ConnectionsList';
import { FacilitiesList } from '../../v2/js/pages/Organization/Facilities';
import { FacilityDrawer } from '../../v2/js/pages/Organization/FacilityDrawer';
import { NeedForm, NeedsList } from '../../v2/js/pages/Organization/Needs';
import OpportunityForm from '../../v2/js/pages/Organization/Opportunities/OpportunityForm';
import OpportunityList from '../../v2/js/pages/Organization/Opportunities/OpportunityList';
import { TimecardDrawer } from '../../v2/js/pages/Organization/Timecards/TimecardDrawer';
import { TimecardsList } from '../../v2/js/pages/Organization/Timecards/TimecardsList';
import { PendingInvitationsList } from '../../v2/js/pages/Organization/Users/PendingInvitationsList';
import { UsersList } from '../../v2/js/pages/Organization/Users/UsersList';
import { AssignmentCreateWrapper as ProviderAssignmentCreateWrapper } from '../../v2/js/pages/Provider/Assignments/AssignmentCreateWrapper';
import { AssignmentDrawer as ProviderAssignmentDrawer } from '../../v2/js/pages/Provider/Assignments/AssignmentDrawer';
import { AssignmentsList as ProviderAssignmentsList } from '../../v2/js/pages/Provider/Assignments/AssignmentsList';
import { ConnectionDrawer as ProviderConnectionDrawer } from '../../v2/js/pages/Provider/Connections/ConnectionDrawer';
import { ProviderConnectionsList } from '../../v2/js/pages/Provider/Connections/ConnectionsList';
import { DocumentsPage } from '../../v2/js/pages/Provider/DocumentsPage';
import { ProviderFacilityDetailsFull } from '../../v2/js/pages/Provider/ProviderFacilityDetailsFull';
import { ProviderHomeWrapper } from '../../v2/js/pages/Provider/ProviderHome/ProviderHomeWrapper';
import { ProviderReferralsWrapper } from '../../v2/js/pages/Provider/Referrals';
import { SourcedProviderRegistration } from '../../v2/js/pages/Provider/Registration/SourcedProviderRegistration';
import { TimecardDrawer as ProviderTimecardDrawer } from '../../v2/js/pages/Provider/Timecards/TimecardDrawer';
import { TimecardsList as ProviderTimecardsList } from '../../v2/js/pages/Provider/Timecards/TimecardsList';
import { PublicDocumentSigning } from '../../v2/js/pages/PublicDocumentSigning';
import {
  StaffAssignmentsList,
  StaffClearancesList,
  StaffConnectionsList,
  StaffFacilitiesList,
  StaffHome,
  StaffIngestFileDetail,
  StaffIngestFilesList,
  StaffNeedsList,
  StaffOpportunitiesList,
  StaffPendingInvitationsList,
  StaffTimecardsList,
  StaffUsersList,
} from '../../v2/js/pages/Staff';
import { UserTypeEnum } from '../api';
import { App } from '../App';
import { EntityProfile } from '../pages/EntityProfile';
import { FacilityRatingPage } from '../pages/FacilityRatingPage';
import { ForgotPassword } from '../pages/ForgotPassword';
import { HealthcareEntityDashboard } from '../pages/HealthcareEntityDashboard';
import { HealthcareEntityProviderList } from '../pages/HealthcareEntityDashboard/HealthcareEntityProviderList';
import { ProviderProfile } from '../pages/HealthcareEntityDashboard/ProviderProfile';
import { Home } from '../pages/Home';
import { HomeRedirect } from '../pages/HomeRedirect';
import { InvitationRegisterPage } from '../pages/InvitationRegisterPage';
import { Login } from '../pages/Login';
import { NotFound } from '../pages/NotFound';
import { PasswordReset } from '../pages/PasswordReset';
import { CurrentProfileEdit } from '../pages/ProviderDashboard/CurrentProviderEdit';
import { ProviderEducation } from '../pages/ProviderDashboard/CurrentProviderEdit/ProviderEducation';
import { ProviderIdentification } from '../pages/ProviderDashboard/CurrentProviderEdit/ProviderIdentification';
import { ProviderLicenses } from '../pages/ProviderDashboard/CurrentProviderEdit/ProviderLicenses';
import { ProviderOtherInfo } from '../pages/ProviderDashboard/CurrentProviderEdit/ProviderOtherInfo';
import { ProviderPersonalInfo } from '../pages/ProviderDashboard/CurrentProviderEdit/ProviderPersonalInfo';
import { ProviderProfessionalTraits } from '../pages/ProviderDashboard/CurrentProviderEdit/ProviderProfessionalTraits';
import { ProviderRates } from '../pages/ProviderDashboard/CurrentProviderEdit/ProviderRates';
import { CurrentProviderProfile } from '../pages/ProviderDashboard/CurrentProviderProfile';
import { Credentials } from '../pages/ProviderDashboard/CurrentProviderProfile/Credentials';
import { Education } from '../pages/ProviderDashboard/CurrentProviderProfile/Education';
import { Licenses } from '../pages/ProviderDashboard/CurrentProviderProfile/Licenses';
import { Other } from '../pages/ProviderDashboard/CurrentProviderProfile/Other';
import { ProfessionalTraits } from '../pages/ProviderDashboard/CurrentProviderProfile/ProfessionalTraits';
import { Reputation } from '../pages/ProviderDashboard/CurrentProviderProfile/Reputation';
import { ProviderQuestionnaire } from '../pages/ProviderQuestionnaire';
import { AccountSetup } from '../pages/Registration/AccountSetup';
import { EmailConfirmation } from '../pages/Registration/PendingEmailConfirmation';
import { SocialSignup } from '../pages/Registration/SocialSignup';
import { SocialLogin } from '../pages/SocialLogin';
import { SwagRedirect } from '../pages/Swag/SwagRedirect';
import { VerifyEmail } from '../pages/VerifyEmail';
import { ALL_USER_TYPES, AuthenticatedRoute } from './AuthenticatedRoute';

const registerRoutes = [
  { path: '/register/complete', element: <SocialSignup /> },
  {
    element: (
      <GoogleReCaptchaProvider reCaptchaKey={window.RECAPTCHA_SITE_KEY}>
        <Outlet />
      </GoogleReCaptchaProvider>
    ),
    children: [
      {
        path: '/register/provider/setup',
        element: (
          <AccountSetup userType={UserTypeEnum.PROVIDER} documentTitle="Provider Account Setup" />
        ),
      },
      {
        path: '/register/provider/sourced',
        element: <SourcedProviderRegistration />,
      },
      {
        path: '/register/rep/setup',
        element: (
          <AccountSetup
            userType={UserTypeEnum.HEALTHCARE_ENTITY_REP}
            documentTitle="Healthcare Organization Account Setup"
          />
        ),
      },
      { path: '/register/email-confirmation', element: <EmailConfirmation /> },
    ],
  },
];

// Redirect components for facility routes - redirect to facilities list with drawer
function FacilityRedirect() {
  const { facilityId } = useParams();
  return <Navigate to={`/app/organization/facilities?id=${facilityId}`} replace />;
}

function FacilityDetailPage() {
  const { facilityId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [referrer] = useState(() => (location.state as { referrer?: string })?.referrer);
  if (!facilityId) return <Navigate to="/app/organization/facilities" replace />;
  return (
    <div className="tw:flex-1 tw:min-h-0 tw:overflow-y-auto tw:px-6 tw:pb-4 tw:w-full tw:md:w-6xl tw:mx-auto">
      <FacilityDrawer
        facilityId={facilityId}
        onClose={() => navigate(referrer || '/app/organization/facilities')}
        isFullPage
      />
    </div>
  );
}

// Redirect old healthcare-entity facilities list to new organization route
function HealthcareEntityFacilitiesListRedirect() {
  return <Navigate to="/app/organization/facilities" replace />;
}

// Redirect old healthcare-entity users list to new organization route
function HealthcareEntityUsersRedirect() {
  return <Navigate to="/app/organization/users" replace />;
}

// Redirect old healthcare-entity pending invitations to new organization route
function HealthcareEntityPendingInvitationsRedirect() {
  return <Navigate to="/app/organization/pending-invitations" replace />;
}

// Redirect index of org provider profile to the first remaining tab
function ProviderProfileIndexRedirect() {
  const { providerId } = useParams();
  return <Navigate to={`/app/healthcare-entity/providers/${providerId}/credentials`} replace />;
}

// Redirect legacy public availability short-link to the new assignment-create flow
function ProviderAvailabilityRedirect() {
  const { token } = useParams();
  return <Navigate to={`/pub/provider/assignments/create/${token ?? ''}`} replace />;
}

// Redirect components for connection routes - redirect to connections list with drawer
function ConnectionRedirect() {
  const { id } = useParams();
  return <Navigate to={`/app/provider/connections?id=${id}`} replace />;
}

// Redirect components for endorsement routes
function EndorsementRedirect() {
  return <Navigate to="/endorsement/provider-reference" replace />;
}

function EndorsementIdRedirect() {
  const { endorsementId } = useParams();
  return <Navigate to={`/endorsement/provider-reference/${endorsementId}`} replace />;
}

// Redirect opportunity view route to opportunity list with drawer
function OpportunityViewRedirect() {
  const { opportunityId } = useParams();
  return <Navigate to={`/app/organization/opportunities?id=${opportunityId}`} replace />;
}

// Redirect components for connection-requests routes
function ProviderConnectionRequestsRedirect() {
  return <Navigate to="/app/provider/connections" replace />;
}

function HealthcareEntityConnectionRequestsRedirect() {
  return <Navigate to="/app/organization/connections" replace />;
}

// Redirect components for legacy booking-requests routes (cutover to assignments)
function ProviderBookingRequestsRedirect() {
  return <Navigate to="/app/provider/assignments" replace />;
}

function HealthcareEntityBookingRequestsRedirect() {
  return <Navigate to="/app/organization/assignments" replace />;
}

function ProviderAssignmentDetailPage() {
  const { assignmentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [referrer] = useState(() => (location.state as { referrer?: string })?.referrer);
  if (!assignmentId) return <Navigate to="/app/provider/assignments" replace />;
  return (
    <div className="tw:flex-1 tw:min-h-0 tw:overflow-y-auto tw:px-6 tw:pb-4 tw:w-full tw:md:w-6xl tw:mx-auto">
      <ProviderAssignmentDrawer
        assignmentId={assignmentId}
        onClose={() => navigate(referrer || '/app/provider/assignments')}
        isFullPage
      />
    </div>
  );
}

function ProviderConnectionDetailPage() {
  const { conn_id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Capture once on load — the drawer's tab/param syncing replaces history
  // entries without state, which would otherwise drop the referrer.
  const [referrer] = useState(() => (location.state as { referrer?: string })?.referrer);
  if (!conn_id) return <Navigate to="/app/provider/connections" replace />;
  return (
    <div className="tw:flex-1 tw:min-h-0 tw:overflow-y-auto tw:px-6 tw:pb-4 tw:w-full tw:md:w-6xl tw:mx-auto">
      <ProviderConnectionDrawer
        connection={undefined as never}
        connectionId={conn_id}
        onClose={() => navigate(referrer || '/app/provider/connections')}
        isFullPage
      />
    </div>
  );
}

function AssignmentDetailPage() {
  const { assignmentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [referrer] = useState(() => (location.state as { referrer?: string })?.referrer);
  if (!assignmentId) return <Navigate to="/app/organization/assignments" replace />;
  return (
    <div className="tw:flex-1 tw:min-h-0 tw:overflow-y-auto tw:px-6 tw:pb-4 tw:w-full tw:md:w-6xl tw:mx-auto">
      <AssignmentDrawer
        assignmentId={assignmentId}
        onClose={() => navigate(referrer || '/app/organization/assignments')}
        isFullPage
      />
    </div>
  );
}

function AssignmentCreatePage() {
  const [searchParams] = useSearchParams();
  return (
    <div className="tw:flex-1 tw:min-h-0 tw:overflow-y-auto">
      <AssignmentCreateWrapper
        providerToken={searchParams.get('token') || undefined}
        initialFacilityId={searchParams.get('facility') || undefined}
      />
    </div>
  );
}

function StaffAssignmentDetailPage() {
  const { assignmentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [referrer] = useState(() => (location.state as { referrer?: string })?.referrer);
  if (!assignmentId) return <Navigate to="/staff/assignments" replace />;
  return (
    <div className="tw:flex-1 tw:min-h-0 tw:overflow-y-auto tw:px-6 tw:pb-4 tw:w-full tw:md:w-6xl tw:mx-auto">
      <AssignmentDrawer
        assignmentId={assignmentId}
        onClose={() => navigate(referrer || '/staff/assignments')}
        isFullPage
      />
    </div>
  );
}

function StaffTimecardDetailPage() {
  const { timecardId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [referrer] = useState(() => (location.state as { referrer?: string })?.referrer);
  if (!timecardId) return <Navigate to="/staff/timecards" replace />;
  return (
    <div className="tw:flex-1 tw:min-h-0 tw:overflow-y-auto tw:px-6 tw:pb-4 tw:w-full tw:md:w-6xl tw:mx-auto">
      <TimecardDrawer
        timecardId={timecardId}
        onClose={() => navigate(referrer || '/staff/timecards')}
        isFullPage
      />
    </div>
  );
}

function TimecardDetailPage() {
  const { timecardId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [referrer] = useState(() => (location.state as { referrer?: string })?.referrer);
  if (!timecardId) return <Navigate to="/app/organization/timecards" replace />;
  return (
    <div className="tw:flex-1 tw:min-h-0 tw:overflow-y-auto tw:px-6 tw:pb-4 tw:w-full tw:md:w-6xl tw:mx-auto">
      <TimecardDrawer
        timecardId={timecardId}
        onClose={() => navigate(referrer || '/app/organization/timecards')}
        isFullPage
      />
    </div>
  );
}

function ProviderTimecardDetailPage() {
  const { timecardId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [referrer] = useState(() => (location.state as { referrer?: string })?.referrer);
  if (!timecardId) return <Navigate to="/app/provider/timecards" replace />;
  return (
    <div className="tw:flex-1 tw:min-h-0 tw:overflow-y-auto tw:px-6 tw:pb-4 tw:w-full tw:md:w-6xl tw:mx-auto">
      <ProviderTimecardDrawer
        timecardId={timecardId}
        onClose={() => navigate(referrer || '/app/provider/timecards')}
        isFullPage
      />
    </div>
  );
}

function StaffConnectionDetailPage() {
  const { conn_id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Capture once on load — the drawer's tab/param syncing replaces history
  // entries without state, which would otherwise drop the referrer.
  const [referrer] = useState(() => (location.state as { referrer?: string })?.referrer);
  if (!conn_id) return <Navigate to="/staff/connections" replace />;
  return (
    <div className="tw:flex-1 tw:min-h-0 tw:overflow-y-auto tw:px-6 tw:pb-4 tw:w-full tw:md:w-6xl tw:mx-auto">
      <OrganizationConnectionDrawer
        connection={undefined as never}
        connectionId={conn_id}
        onClose={() => navigate(referrer || '/staff/connections')}
        linkContext="staff"
        isFullPage
      />
    </div>
  );
}

function OrganizationConnectionDetailPage() {
  const { conn_id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Capture once on load — the drawer's tab/param syncing replaces history
  // entries without state, which would otherwise drop the referrer.
  const [referrer] = useState(() => (location.state as { referrer?: string })?.referrer);
  if (!conn_id) return <Navigate to="/app/organization/connections" replace />;
  return (
    <div className="tw:flex-1 tw:min-h-0 tw:overflow-y-auto tw:px-6 tw:pb-4 tw:w-full tw:md:w-6xl tw:mx-auto">
      <OrganizationConnectionDrawer
        connection={undefined as never}
        connectionId={conn_id}
        onClose={() => navigate(referrer || '/app/organization/connections')}
        isFullPage
      />
    </div>
  );
}

export const routes: ReturnType<typeof createBrowserRouter> = createBrowserRouter([
  {
    element: <App />,
    children: [
      { path: '/', element: <Home /> },
      { path: '/home', element: <Home /> },
      { path: '/login', element: <Login /> },
      { path: '/social-login/:socialApp', element: <SocialLogin /> },
      { path: '/invitation/:invitationId', element: <InvitationRegisterPage /> },
      { path: '/verify-email/:key', element: <VerifyEmail /> },
      { path: '/forgot-password', element: <ForgotPassword /> },
      { path: '/password-reset/:uid/:token', element: <PasswordReset /> },
      { path: '/swag', element: <SwagRedirect /> },
      // Old endorsement routes - redirect to new paths
      // can be deprecated once no more /endorsement routes are in use
      { path: '/endorsement', element: <EndorsementRedirect /> },
      { path: '/endorsement/:endorsementId', element: <EndorsementIdRedirect /> },
      { path: '/endorsement/provider-reference', element: <ProviderReferenceWrapper /> },
      { path: '/endorsement/provider-reference/:id', element: <ProviderReferenceWrapper /> },
      { path: '/endorsement/provider-survey', element: <ProviderSurveyWrapper /> },
      { path: '/endorsement/provider-survey/:id', element: <ProviderSurveyWrapper /> },
      ...registerRoutes,
      {
        path: '/staff',
        element: <AuthenticatedRoute allowedUserTypes={[UserTypeEnum.STAFF]} />,
        children: [
          { path: '/staff/home', element: <StaffHome /> },
          { path: '/staff/assignments', element: <StaffAssignmentsList /> },
          { path: '/staff/assignments/:assignmentId', element: <StaffAssignmentDetailPage /> },
          { path: '/staff/timecards', element: <StaffTimecardsList /> },
          { path: '/staff/timecards/:timecardId', element: <StaffTimecardDetailPage /> },
          { path: '/staff/clearance', element: <StaffClearancesList /> },
          { path: '/staff/connections', element: <StaffConnectionsList /> },
          { path: '/staff/connections/:conn_id', element: <StaffConnectionDetailPage /> },
          { path: '/staff/facilities', element: <StaffFacilitiesList /> },
          { path: '/staff/needs', element: <StaffNeedsList /> },
          { path: '/staff/opportunities', element: <StaffOpportunitiesList /> },
          { path: '/staff/users', element: <StaffUsersList /> },
          { path: '/staff/pending-invitations', element: <StaffPendingInvitationsList /> },
          { path: '/staff/ingest', element: <StaffIngestFilesList /> },
          { path: '/staff/ingest/:ingestFileId', element: <StaffIngestFileDetail /> },
        ],
      },
      {
        path: '/pub',
        element: <Outlet />,
        children: [
          {
            path: '/pub/provider/assignments/create/:token',
            element: <ProviderAssignmentCreateWrapper />,
          },
          {
            path: '/pub/provider/availability/:token',
            element: <ProviderAvailabilityRedirect />,
          },
          {
            path: '/pub/documents/sign/:signingToken',
            element: <PublicDocumentSigning />,
          },
        ],
      },
      {
        path: '/app',
        element: <AuthenticatedRoute allowedUserTypes={ALL_USER_TYPES} />,
        children: [
          { path: '/app', element: <HomeRedirect /> },
          {
            path: '/app/provider',
            element: <AuthenticatedRoute allowedUserTypes={[UserTypeEnum.PROVIDER]} />,
            children: [
              { path: '/app/provider/', element: <HomeRedirect /> },
              { path: '/app/provider/onboarding', element: <ProviderOnboardingWrapper /> },
              { path: '/app/provider/home', element: <ProviderHomeWrapper /> },
              { path: '/app/provider/documents', element: <DocumentsPage /> },
              {
                path: '/app/provider/facility-details/:facilityId',
                element: <ProviderFacilityDetailsFull />,
              },
              {
                path: '/app/provider/profile',
                element: <CurrentProviderProfile />,
                children: [
                  {
                    path: '/app/provider/profile/',
                    element: <Navigate to="/app/provider/profile/credentials" replace />,
                  },
                  { path: '/app/provider/profile/credentials', element: <Credentials /> },
                  {
                    path: '/app/provider/profile/professional-traits',
                    element: <ProfessionalTraits />,
                  },
                  { path: '/app/provider/profile/education', element: <Education /> },
                  { path: '/app/provider/profile/licenses', element: <Licenses /> },
                  { path: '/app/provider/profile/reputation', element: <Reputation /> },
                  { path: '/app/provider/profile/other', element: <Other /> },
                ],
              },
              {
                path: '/app/provider/edit',
                element: <CurrentProfileEdit />,
                children: [
                  { path: '/app/provider/edit', element: <ProviderPersonalInfo /> },
                  {
                    path: '/app/provider/edit/rates',
                    element: <ProviderRates />,
                  },
                  { path: '/app/provider/edit/education', element: <ProviderEducation /> },
                  { path: '/app/provider/edit/licenses', element: <ProviderLicenses /> },
                  { path: '/app/provider/edit/other', element: <ProviderOtherInfo /> },
                  {
                    path: '/app/provider/edit/professional-traits',
                    element: <ProviderProfessionalTraits />,
                  },
                  {
                    path: '/app/provider/edit/identification',
                    element: <ProviderIdentification />,
                  },
                ],
              },
              { path: '/app/provider/connections', element: <ProviderConnectionsList /> },
              {
                path: '/app/provider/connections/:conn_id',
                element: <ProviderConnectionDetailPage />,
              },
              // DEPRECATE: remove this once frontend/js/pages directory is fully replaced
              {
                path: '/app/provider/connection-requests',
                element: <ProviderConnectionRequestsRedirect />,
              },
              {
                path: '/app/provider/connection-requests/:id',
                element: <ConnectionRedirect />,
              },
              {
                path: '/app/provider/booking-requests/',
                element: <ProviderBookingRequestsRedirect />,
              },
              {
                path: '/app/provider/booking-requests/:bookingId',
                element: <ProviderBookingRequestsRedirect />,
              },
              {
                path: '/app/provider/assignments',
                element: <ProviderAssignmentsList />,
              },
              {
                path: '/app/provider/assignments/:assignmentId',
                element: <ProviderAssignmentDetailPage />,
              },
              {
                path: '/app/provider/timecards',
                element: <ProviderTimecardsList />,
              },
              {
                path: '/app/provider/timecards/:timecardId',
                element: <ProviderTimecardDetailPage />,
              },
              {
                path: '/app/provider/facility-rating/:facilityId',
                element: <FacilityRatingPage />,
              },
              {
                path: '/app/provider/questionnaire/:providerId',
                element: <ProviderQuestionnaire />,
              },
              { path: '/app/provider/referrals', element: <ProviderReferralsWrapper /> },
            ],
          },
          {
            path: '/app/organization/',
            element: (
              <AuthenticatedRoute
                allowedUserTypes={[UserTypeEnum.HEALTHCARE_ENTITY_REP, UserTypeEnum.STAFF]}
              />
            ),
            children: [
              {
                path: '/app/organization/',
                element: <HealthcareEntityDashboard />,
                children: [
                  {
                    // deprecate this redirect
                    path: '/app/organization/opportunities/:opportunityId',
                    element: <OpportunityViewRedirect />,
                  },
                  {
                    path: '/app/organization/opportunities/new',
                    element: (
                      <AuthenticatedRoute permissionRequired="create__assignments__opportunity" />
                    ),
                    children: [
                      {
                        path: '/app/organization/opportunities/new',
                        element: <OpportunityForm />,
                      },
                    ],
                  },
                  {
                    path: '/app/organization/opportunities/:opportunityId/edit',
                    element: (
                      <AuthenticatedRoute permissionRequired="update__assignments__opportunity" />
                    ),
                    children: [
                      {
                        path: '/app/organization/opportunities/:opportunityId/edit',
                        element: <OpportunityForm />,
                      },
                    ],
                  },
                  {
                    path: '/app/organization/opportunities',
                    element: (
                      <AuthenticatedRoute permissionRequired="list__assignments__opportunity" />
                    ),
                    children: [
                      {
                        path: '/app/organization/opportunities',
                        element: <OpportunityList />,
                      },
                    ],
                  },
                  {
                    path: '/app/organization/needs/new',
                    element: <AuthenticatedRoute permissionRequired="create__staffing__need" />,
                    children: [
                      {
                        path: '/app/organization/needs/new',
                        element: <NeedForm />,
                      },
                    ],
                  },
                  {
                    path: '/app/organization/needs/:needId/edit',
                    element: <AuthenticatedRoute permissionRequired="update__staffing__need" />,
                    children: [
                      {
                        path: '/app/organization/needs/:needId/edit',
                        element: <NeedForm />,
                      },
                    ],
                  },
                  {
                    path: '/app/organization/needs',
                    element: <AuthenticatedRoute permissionRequired="list__staffing__need" />,
                    children: [
                      {
                        path: '/app/organization/needs',
                        element: <NeedsList />,
                      },
                    ],
                  },
                  {
                    path: '/app/organization/facilities',
                    element: (
                      <AuthenticatedRoute permissionRequired="list__healthcare_entities__healthcarefacility" />
                    ),
                    children: [
                      {
                        path: '/app/organization/facilities',
                        element: <FacilitiesList />,
                      },
                    ],
                  },
                  {
                    path: '/app/organization/facilities/:facilityId',
                    element: (
                      <AuthenticatedRoute permissionRequired="list__healthcare_entities__healthcarefacility" />
                    ),
                    children: [
                      {
                        path: '/app/organization/facilities/:facilityId',
                        element: <FacilityDetailPage />,
                      },
                    ],
                  },
                  {
                    path: '/app/organization/connections',
                    element: (
                      <AuthenticatedRoute permissionRequired="list__connections__connectedfacility" />
                    ),
                    children: [
                      {
                        path: '/app/organization/connections',
                        element: <OrganizationConnectionsList />,
                      },
                    ],
                  },
                  {
                    path: '/app/organization/connections/:conn_id',
                    element: (
                      <AuthenticatedRoute permissionRequired="read__connections__connectedfacility" />
                    ),
                    children: [
                      {
                        path: '/app/organization/connections/:conn_id',
                        element: <OrganizationConnectionDetailPage />,
                      },
                    ],
                  },
                  {
                    path: '/app/organization/assignments',
                    element: (
                      <AuthenticatedRoute permissionRequired="read__assignments__assignment" />
                    ),
                    children: [
                      {
                        path: '/app/organization/assignments',
                        element: <AssignmentsList />,
                      },
                    ],
                  },
                  {
                    path: '/app/organization/assignments/create',
                    element: (
                      <AuthenticatedRoute permissionRequired="create__assignments__assignment" />
                    ),
                    children: [
                      {
                        path: '/app/organization/assignments/create',
                        element: <AssignmentCreatePage />,
                      },
                    ],
                  },
                  {
                    path: '/app/organization/assignments/:assignmentId',
                    element: (
                      <AuthenticatedRoute permissionRequired="read__assignments__assignment" />
                    ),
                    children: [
                      {
                        path: '/app/organization/assignments/:assignmentId',
                        element: <AssignmentDetailPage />,
                      },
                    ],
                  },
                  {
                    path: '/app/organization/timecards',
                    element: (
                      <AuthenticatedRoute permissionRequired="read__assignments__timecard" />
                    ),
                    children: [
                      {
                        path: '/app/organization/timecards',
                        element: <TimecardsList />,
                      },
                    ],
                  },
                  {
                    path: '/app/organization/timecards/:timecardId',
                    element: (
                      <AuthenticatedRoute permissionRequired="read__assignments__timecard" />
                    ),
                    children: [
                      {
                        path: '/app/organization/timecards/:timecardId',
                        element: <TimecardDetailPage />,
                      },
                    ],
                  },
                  {
                    path: '/app/organization/clearance',
                    element: (
                      <AuthenticatedRoute permissionRequired="list__connections__clearance" />
                    ),
                    children: [
                      {
                        path: '/app/organization/clearance',
                        element: <ClearanceList />,
                      },
                    ],
                  },
                  {
                    path: '/app/organization/users',
                    element: (
                      <AuthenticatedRoute permissionRequired="list__healthcare_entities__healthcareentityrep" />
                    ),
                    children: [
                      {
                        path: '/app/organization/users',
                        element: <UsersList />,
                      },
                    ],
                  },
                  {
                    path: '/app/organization/pending-invitations',
                    element: (
                      <AuthenticatedRoute permissionRequired="list__healthcare_entities__healthcareentityrep" />
                    ),
                    children: [
                      {
                        path: '/app/organization/pending-invitations',
                        element: <PendingInvitationsList />,
                      },
                    ],
                  },
                ],
              },
            ],
          },
          {
            path: '/app/healthcare-entity/',
            element: (
              <AuthenticatedRoute
                allowedUserTypes={[UserTypeEnum.HEALTHCARE_ENTITY_REP, UserTypeEnum.STAFF]}
              />
            ),
            children: [
              {
                path: '/app/healthcare-entity/',
                element: <HealthcareEntityDashboard />,
                children: [
                  { path: '/app/healthcare-entity/', element: <HomeRedirect /> },
                  {
                    path: '/app/healthcare-entity/providers',
                    element: <HealthcareEntityProviderList />,
                  },
                  {
                    path: '/app/healthcare-entity/providers/:providerId',
                    element: <ProviderProfile />,
                    children: [
                      {
                        path: '/app/healthcare-entity/providers/:providerId/',
                        element: <ProviderProfileIndexRedirect />,
                      },
                      {
                        path: '/app/healthcare-entity/providers/:providerId/credentials',
                        element: <Credentials />,
                      },
                      {
                        path: '/app/healthcare-entity/providers/:providerId/professional-traits',
                        element: <ProfessionalTraits />,
                      },
                      {
                        path: '/app/healthcare-entity/providers/:providerId/education',
                        element: <Education />,
                      },
                      {
                        path: '/app/healthcare-entity/providers/:providerId/licenses',
                        element: <Licenses />,
                      },
                      {
                        path: '/app/healthcare-entity/providers/:providerId/other',
                        element: <Other />,
                      },
                    ],
                  },
                  {
                    path: '/app/healthcare-entity/providers/:providerId/questionnaire/',
                    element: <ProviderQuestionnaire />,
                  },
                  // DEPRECATE: remove this once frontend/js/pages directory is fully replaced
                  {
                    path: '/app/healthcare-entity/connection-requests',
                    element: <HealthcareEntityConnectionRequestsRedirect />,
                  },
                  {
                    path: '/app/healthcare-entity/booking-requests/',
                    element: <HealthcareEntityBookingRequestsRedirect />,
                  },
                  {
                    path: '/app/healthcare-entity/booking-requests/:bookingId',
                    element: <HealthcareEntityBookingRequestsRedirect />,
                  },
                  {
                    path: '/app/healthcare-entity/facilities',
                    element: <HealthcareEntityFacilitiesListRedirect />,
                  },
                  {
                    path: '/app/healthcare-entity/facilities/:facilityId/edit',
                    element: (
                      <AuthenticatedRoute permissionRequired="update__healthcare_entities__healthcarefacility" />
                    ),
                    children: [
                      {
                        path: '/app/healthcare-entity/facilities/:facilityId/edit',
                        element: <FacilityRedirect />,
                      },
                    ],
                  },
                  {
                    path: '/app/healthcare-entity/users',
                    element: <HealthcareEntityUsersRedirect />,
                  },
                  {
                    path: '/app/healthcare-entity/pending-invitations',
                    element: <HealthcareEntityPendingInvitationsRedirect />,
                  },
                ],
              },
            ],
          },
        ],
      },
      { path: '/app/healthcare-entity/:entityId', element: <EntityProfile /> },
      {
        path: '/app/healthcare-entity/facilities/:facilityId',
        element: <FacilityRedirect />,
      },
      { path: '*', element: <NotFound /> },
    ],
  },
]);
